import { JSX, useState } from 'react';
import { generatePath } from 'react-router';

import { DueDiligenceAssessmentConfigurationApi } from 'Api/TPRM/DueDiligenceAssessmentConfigurationApi';
import { Button } from 'Components/Buttons/Buttons';
import { OverflowMenu, OverflowMenuProps } from 'Components/Buttons/OverflowMenu';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageDataLoader } from 'Components/PageDataLoader/PageDataLoader';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { Text } from 'Components/Text/Text';
import { UsersModal } from 'Components/UcrmTprm/UsersModal/UsersModal';
import { ICON_ADD_CREATE, ICON_DELETE_REMOVE } from 'Config/Icons';
import { SETTINGS, TPRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION, TPRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION_LISTING, UCRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION } from 'Config/PathTemplates';
import { DueDiligenceAssessmentConfiguration } from 'Models/DueDiligence';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';

import { AddAssessmentModal } from './Modals/AddAssessmentModal';

export interface DueDiligenceAssessmentSettingsListingLoaderProps {
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    module: DueDiligenceModule;
}

export const DueDiligenceAssessmentSettingsListingLoader = (props: DueDiligenceAssessmentSettingsListingLoaderProps): JSX.Element => {
    return <PageDataLoader path={TPRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION_LISTING} fetchData={() => props.dueDiligenceAssessmentConfigurationApi.getAllAssessmentConfigurations()} renderForm={(result) => <DueDiligenceAssessmentSettingsListing configurations={result.data} dueDiligenceAssessmentConfigurationApi={props.dueDiligenceAssessmentConfigurationApi} module={props.module} />} />;
};

export interface DueDiligenceAssessmentSettingsListingProps {
    configurations: DueDiligenceAssessmentConfiguration[];
    dueDiligenceAssessmentConfigurationApi: DueDiligenceAssessmentConfigurationApi;
    module: DueDiligenceModule;
}

type DisplayedModal =
    | {
          type: 'addNewAssessment';
      }
    | {
          type: 'delegates';
          delegateIds: string[];
      };

const DueDiligenceAssessmentSettingsListing = (props: DueDiligenceAssessmentSettingsListingProps): JSX.Element => {
    const cachedData = useCachedData();

    const [assessmentToDelete, setAssessmentToDelete] = useState<DueDiligenceAssessmentConfiguration>();
    const [configurations, setConfigurations] = useState(props.configurations);
    const [displayedModal, setDisplayedModal] = useState<DisplayedModal>();

    const columns: ColumnConfig<DueDiligenceAssessmentConfiguration>[] = [
        {
            type: ColumnType.STRING,
            name: 'Name',
            getString: (assessment) => assessment.name,
            getOnClickTextAction: (assessment) => ({
                goToLocation: generatePath(props.module === ProductModule.UCRM ? UCRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION : TPRM_DUE_DILIGENCE_ASSESSMENT_CONFIGURATION, { assessmentId: assessment.id }),
            }),
        },
        {
            type: ColumnType.USER,
            name: 'Default Owner',
            getUserId: (assessment) => assessment.default_owner_id,
            users: cachedData.users,
        },
        {
            type: ColumnType.MULTIPLE_USERS,
            name: 'Default Delegates',
            getUserIds: (assessment) => assessment.default_delegate_ids,
            noun: 'delegate',
            onClick: (delegateIds) => setDisplayedModal({ type: 'delegates', delegateIds }),
        },
        {
            type: ColumnType.CUSTOM,
            name: 'Is Default Assessment',
            getValue: (assessment) => assessment.is_default,
            getTableCellContent: (assessment) => <Text noStyles>{assessment.is_default ? 'Yes' : 'No'}</Text>,
            sortComparator: (a: boolean, b: boolean) => (a === b ? 0 : a ? -1 : 1),
        },
    ];

    const getExistingAssessmentNames = (): string[] => {
        return configurations.map((a) => a.name);
    };

    const getOverflowMenuForRow = (assessment: DueDiligenceAssessmentConfiguration): JSX.Element => {
        const overflowItems: OverflowMenuProps['overflowItems'] = [
            {
                text: 'Delete Assessment',
                icon: ICON_DELETE_REMOVE,
                onClickAction: () => setAssessmentToDelete(assessment),
            },
        ];

        return <OverflowMenu overflowItems={overflowItems} accessibilityTitle={`${assessment.name} options`} />;
    };

    const handleDeleteAssessment = async (): Promise<string> => {
        await props.dueDiligenceAssessmentConfigurationApi.deleteAssessmentConfiguration(assessmentToDelete!.id);
        setConfigurations((prev) => prev.filter((a) => a.id !== assessmentToDelete!.id));
        return 'Assessment deleted.';
    };

    return (
        <>
            {displayedModal?.type === 'addNewAssessment' && <AddAssessmentModal dueDiligenceAssessmentConfigurationApi={props.dueDiligenceAssessmentConfigurationApi} hideModal={() => setDisplayedModal(undefined)} module={props.module} existingNames={getExistingAssessmentNames()} onSaved={(newAssessment) => setConfigurations([...configurations, newAssessment])} />}
            {displayedModal?.type === 'delegates' && <UsersModal header="Delegates" userIds={displayedModal.delegateIds} hideModal={() => setDisplayedModal(undefined)} />}

            {assessmentToDelete && (
                <ConfirmationModal operationType={ConfirmationModalOperationType.DELETE_AFTER_TYPING_TO_CONFIRM} headerText="Delete Assessment" areYouSureText={`Are you sure you want to delete "${assessmentToDelete.name}"?`} performOperation={handleDeleteAssessment} hideModal={() => setAssessmentToDelete(undefined)}>
                    <Text>All control frameworks, control groups, controls, and questions will be deleted.</Text>
                </ConfirmationModal>
            )}
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={generatePath(SETTINGS)}>Settings</BreadcrumbLink>
                        <BreadcrumbText>Due Diligence Assessments Configuration</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle={`${props.module === ProductModule.UCRM ? 'Use Case' : 'Third-Party'} Due Diligence Assessments Configuration`}
                headerDescription={`Configure the assessments that can be sent to ${props.module === ProductModule.UCRM ? 'questionnaire respondents' : 'third-party service contacts'} when performing due diligence.`}
                body={[
                    {
                        title: 'Assessments',
                        buttons: (
                            <Button variant="secondary" onClick={() => setDisplayedModal({ type: 'addNewAssessment' })} fontAwesomeImage={ICON_ADD_CREATE}>
                                Add Assessment
                            </Button>
                        ),
                        content: <Table columns={columns} defaultSortColumn="Name" filters={[]} getUniqueIdForRow={(assessment) => assessment.id} rowsData={configurations} getOverflowMenuForRow={getOverflowMenuForRow} />,
                    },
                ]}
            />
        </>
    );
};
